import styles from './CheckoutCouponSection.module.scss';
import { useEffect, useState } from 'react';
import { Button } from 'src/v2/commonComponents/Button';
import { SVG_SIZE_M, SVG_SIZE_S } from 'src/constants';
import { Info, X } from '@phosphor-icons/react';
import cn from 'classnames';
import { useQueryParams } from 'src/hooks';
import { useFeatureFlagEnabled } from 'posthog-js/react';

type CheckoutCouponSectionProps = {
  hasError: boolean;
  onChangeAppliedValue: (value: string | undefined) => void;
  isValueApplied: boolean;
  isLoading?: boolean;
  appliedCouponValue?: string;
};

export const CheckoutCouponSection = ({
  hasError,
  onChangeAppliedValue,
  isValueApplied,
  isLoading,
  appliedCouponValue,
}: CheckoutCouponSectionProps) => {
  const { searchParams } = useQueryParams();
  const [inputValue, changeInputValue] = useState<string>('');

  const isThanksgivingCouponEnabled =
    useFeatureFlagEnabled('thanksgivingCoupon');
  const isThanksgivingCoupon = inputValue
    .toLowerCase()
    .startsWith('ninjathanks');

  useEffect(() => {
    if (!!searchParams.coupon) {
      changeInputValue(searchParams.coupon);
    }
  }, [searchParams.coupon]);

  useEffect(() => {
    if (appliedCouponValue) {
      changeInputValue(appliedCouponValue);
    }
  }, [appliedCouponValue]);

  const handleChangeValue = (value: string) => {
    if (hasError) {
      onChangeAppliedValue(undefined);
    }
    changeInputValue(value);
  };

  const handleResetValue = () => {
    changeInputValue('');
    onChangeAppliedValue(undefined);
  };

  const handleApplyClick = () => {
    if (isThanksgivingCoupon && !isThanksgivingCouponEnabled) {
      return;
    }
    onChangeAppliedValue(inputValue);
  };

  const showError =
    (hasError || (isThanksgivingCoupon && !isThanksgivingCouponEnabled)) &&
    !isLoading;

  return (
    <div className={styles.sectionWrapper}>
      <div className={styles.labelWrapper}>
        <label className={styles.label}>Coupon code</label>
        <div className={styles.description}>
          <Info size={SVG_SIZE_S} />
          <span>
            Can be used with all payments <b>except PayPal</b>
          </span>
        </div>
      </div>
      {!isValueApplied || hasError ? (
        <div className={styles.inputWithErrorWrapper}>
          <div className={styles.inputWrapper}>
            <input
              className={cn(styles.input, { [styles.hasError]: showError })}
              value={inputValue}
              placeholder="Enter coupon here"
              onChange={(e) => handleChangeValue(e.target.value)}
            />
            <Button
              color="link"
              className={styles.applyButton}
              onClick={handleApplyClick}
              disabled={!inputValue}
            >
              Apply
            </Button>
          </div>
          {showError && (
            <span className={styles.errorText}>
              Coupon not valid for selected plan. Try a different plan
            </span>
          )}
        </div>
      ) : (
        <div className={styles.appliedValueWrapper}>
          <div className={styles.appliedValueText}>
            <h4 className={styles.appliedCouponTitle}>{inputValue}</h4>
            <span className={styles.appliedCouponDescription}>
              Coupon Applied
            </span>
          </div>
          <Button
            color="transparent"
            className={styles.resetButton}
            onClick={handleResetValue}
          >
            <X size={SVG_SIZE_M} />
          </Button>
        </div>
      )}
    </div>
  );
};
